// ABOUTME: Watches the live microphone MediaStreamTrack — logs which device was captured and calls
// ABOUTME: `restart` when the track ends or stays muted (OS device switch, another app took the mic).
//
// Why: a "connected, pill on, visualizer flat, agent hears nothing" mic is a capture graph whose
// source went silent underneath it. Chrome reports that as the track's `mute`/`ended` events; the
// analyser then reads zeros and nothing in the app knew. Sporadic on Windows (default-device
// changes, Bluetooth headsets, Teams/Zoom grabbing the device). This module makes it visible in
// the console and self-heals by rebuilding the capture graph.
//
// Manual test case: start a session, speak (visualizer moves), then in Windows Sound settings
// switch the default input device. Expected: console logs `[mic] track ended/muted — restarting
// capture`, a new `[mic] capturing from "<device>"` line, and the visualizer moves again within
// ~2 s without touching the UI.

export const MUTE_GRACE_MS = 1500;

/** How many capture restarts the health monitor may trigger inside one window before giving up. */
export const RESTART_BUDGET_MAX = 3;
/** The window those restarts are counted over. */
export const RESTART_BUDGET_WINDOW_MS = 60000;

/**
 * A cap on automatic restarts, so a source that comes back dead every time (a microphone muted at
 * the OS level, a device that never delivers frames) cannot loop getUserMedia + AudioContext every
 * `MUTE_GRACE_MS` for the rest of the session. `allow()` answers "may I restart now?" and counts
 * the attempt; the count resets once `windowMs` has passed since the first attempt in the window.
 * The caller decides what an exhausted budget means (App.js turns the mic OFF so the pill tells
 * the truth instead of showing a live mic that hears nothing).
 */
export function createRestartBudget({ max = RESTART_BUDGET_MAX, windowMs = RESTART_BUDGET_WINDOW_MS, now = Date.now } = {}) {
  let count = 0;
  let windowStart = 0;
  return {
    allow() {
      const t = now();
      if (count === 0 || t - windowStart >= windowMs) {
        count = 0;
        windowStart = t;
      }
      if (count >= max) return false;
      count += 1;
      return true;
    },
    reset() {
      count = 0;
      windowStart = 0;
    },
  };
}

/**
 * Attach health listeners to a mic track. Returns a detach function — call it BEFORE stopping
 * the track yourself, so a deliberate stop is never mistaken for a failure.
 */
export function attachMicTrackHealth(track, { restart, log = console } = {}) {
  if (!track || typeof track.addEventListener !== 'function') return () => {};
  const settings = typeof track.getSettings === 'function' ? track.getSettings() : {};
  log.info && log.info(`[mic] capturing from "${track.label || 'unknown device'}" (${settings.sampleRate || '?'} Hz, muted=${!!track.muted})`);

  let muteTimer = null;
  let detached = false;
  const onMute = () => {
    log.warn && log.warn('[mic] track muted by the OS/browser');
    clearTimeout(muteTimer);
    muteTimer = setTimeout(() => {
      if (!detached && track.muted) {
        log.warn && log.warn('[mic] still muted — restarting capture');
        restart && restart('muted');
      }
    }, MUTE_GRACE_MS);
  };
  const onUnmute = () => {
    clearTimeout(muteTimer);
    log.info && log.info('[mic] track unmuted');
  };
  const onEnded = () => {
    if (detached) return;
    log.warn && log.warn('[mic] track ended — restarting capture');
    restart && restart('ended');
  };
  track.addEventListener('mute', onMute);
  track.addEventListener('unmute', onUnmute);
  track.addEventListener('ended', onEnded);
  if (track.muted) onMute();
  return () => {
    detached = true;
    clearTimeout(muteTimer);
    track.removeEventListener('mute', onMute);
    track.removeEventListener('unmute', onUnmute);
    track.removeEventListener('ended', onEnded);
  };
}
